import { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import moment from 'moment';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFolderImage } from '@fortawesome/pro-duotone-svg-icons';
import { faPlus, faGripLines, faSort } from '@fortawesome/free-solid-svg-icons';

import { Button, Input, Switch, Table, Tooltip, Typography, notification } from 'antd';
import type { SortableContainerProps, SortEnd } from 'react-sortable-hoc';
import { SortableContainer, SortableElement, SortableHandle } from 'react-sortable-hoc';

import { txt } from '../../../../locales/i18n';
import { SharedTable } from '../../../shared/table';
import { ImageCatalogue } from '../../../shared/cms/image-catalogue';
import * as ExperienceService from '../../../../services/bb-api/oplevalt/experience';
import * as ImageService from '../../../../services/bb-api/oplevalt/image';
import * as OplevaltContext from '../context';

const DragHandle = SortableHandle(() => <FontAwesomeIcon icon={faGripLines} />);
const SortableItem = SortableElement((props: any) => <tr {...props} />);
const SortableBody = SortableContainer((props: any) => <tbody {...props} />);

export function updateListSort(list: any[], fromIndex: number, toIndex: number) {
	const sortedList = [...list];

	const startIndex = fromIndex < 0 ? sortedList.length + fromIndex : fromIndex;

	if (startIndex >= 0 && startIndex < sortedList.length) {
		const endIndex = toIndex < 0 ? sortedList.length + toIndex : toIndex;

		const [item] = sortedList.splice(fromIndex, 1);
		sortedList.splice(endIndex, 0, item);
	}

	return sortedList;
}

export const ExperiencesPage = () => {
	const [experiences, setExperiences] = useState([]);
	const [isLoading, setIsLoading] = useState(true);
	const [openImageCatalogue, setOpenImageCatalogue] = useState(false);
	const [releaseLoaders, setReleaseLoaders] = useState(new Set());
	const [searchText, setSearchText] = useState('');
	const history = useHistory();

	const getExperiences = useCallback(async () => {
		setIsLoading(true);

		try {
			const experiences = await ExperienceService.search(searchText);
			setExperiences(experiences);
		} catch (error) {
			const message = txt('oplevalt.notifications.unknown error');
			notification.error({ message });
		}

		setIsLoading(false);
	}, [searchText]);

	useEffect(() => {
		const timeout = setTimeout(getExperiences, 350);
		return () => clearTimeout(timeout);
	}, [getExperiences, searchText]);

	const onSortEnd = async ({ oldIndex, newIndex }: SortEnd) => {
		if (oldIndex === newIndex) return;

		const experiencesSorted = updateListSort(experiences.slice(), oldIndex, newIndex);
		const experiencesFilted = experiencesSorted.filter((experience: any) => {
			return Boolean(experience);
		});

		const experienceIds = experiencesFilted.map(({ id }) => id);
		setIsLoading(true);

		try {
			const experiencesUpdated = await ExperienceService.updateOrder(experienceIds);
			setExperiences(experiencesUpdated);
		} catch (error) {
			const message = txt('oplevalt.notifications.order update failed');
			notification.error({ message });
		}

		setIsLoading(false);
	};

	const DraggableContainer = (props: SortableContainerProps) => (
		<SortableBody
			useDragHandle
			disableAutoscroll
			helperClass="oplevalt-row-dragging"
			onSortEnd={onSortEnd}
			{...props}
		/>
	);

	const DraggableBodyRow: React.FC<any> = ({ className, style, ...props }) => {
		const index = experiences.findIndex((experience: any) => {
			return experience.id === props['data-row-key'];
		});

		return <SortableItem index={index} {...props} />;
	};

	const renderDate = (value: string | Date): string => {
		const format = 'ddd DD/MM-YY HH:mm';
		return moment(value).format(format);
	};

	const renderReleased = (experience: any) => {
		const experienceId = experience.id;

		const changeHandler = async (released: boolean) => {
			setReleaseLoaders((releaseLoaders) => {
				releaseLoaders.add(experienceId);
				return new Set(releaseLoaders);
			});

			try {
				await ExperienceService.toggleExperienceRelease(experienceId, released);
				const experiences = await ExperienceService.search();
				setExperiences(experiences);
			} catch (error) {
				const message = txt('oplevalt.notifications.update failed');
				notification.error({ message });
			}

			setReleaseLoaders((releaseLoaders) => {
				releaseLoaders.delete(experience.id);
				return new Set(releaseLoaders);
			});
		};

		return (
			<Switch
				loading={releaseLoaders.has(experience.id)}
				className="released-switch"
				checked={experience.released}
				onChange={changeHandler}
			/>
		);
	};

	const renderOpenButton = (experience: any) => (
		<Button type="primary" href={'experiences/' + experience.id}>
			{txt('oplevalt.open')}
		</Button>
	);

	const renderLink = (experience: any) => {
		const query = experience.released ? '' : '?preview';
		const oplevaltHost = process.env.REACT_APP_OPLEVALT_HOST;
		const experienceUrl = oplevaltHost + '/oplevelser/' + experience.url + query;

		return (
			<Tooltip title={txt('oplevalt.experience')} placement="right">
				<Typography.Link href={experienceUrl} target="_blank" ellipsis>
					{'/oplevelser/' + experience.url}
				</Typography.Link>
			</Tooltip>
		);
	};

	return (
		<MainStyled>
			<div className="header-container">
				<Tooltip title={txt('oplevalt.search')}>
					<Input
						allowClear
						placeholder={txt('oplevalt.search')}
						value={searchText}
						onChange={({ target }) => setSearchText(target.value)}
					/>
				</Tooltip>

				<span>
					<Tooltip title={txt('oplevalt.create')}>
						<Button type="link" onClick={() => history.push('experiences/$')}>
							<FontAwesomeIcon icon={faPlus} size="lg" />
						</Button>
					</Tooltip>

					<Tooltip title={txt('global.images')}>
						<Button type="link" onClick={() => setOpenImageCatalogue(true)}>
							<FontAwesomeIcon icon={faFolderImage} size="lg" />
						</Button>
					</Tooltip>
				</span>
			</div>

			<SharedTable
				rowKey="id"
				dataSource={experiences}
				loading={isLoading}
				components={{
					body: {
						wrapper: DraggableContainer,
						row: DraggableBodyRow,
					},
				}}
			>
				{searchText === '' && (
					<Table.Column
						width={60}
						dataIndex="sort"
						title={<FontAwesomeIcon icon={faSort} />}
						className="drag-visible"
						render={() => <DragHandle />}
						align="center"
					/>
				)}

				<Table.Column
					width={120}
					render={renderOpenButton}
					title={txt('oplevalt.open')}
					align="center"
				/>

				<Table.Column
					width={90}
					align="center"
					title={txt('oplevalt.released')}
					render={renderReleased}
				/>

				<Table.Column
					dataIndex={['content', 'title']}
					title={txt('oplevalt.title')}
					width={240}
					ellipsis
				/>

				<Table.Column
					dataIndex={['content', 'subtitle']}
					title={txt('oplevalt.subtitle')}
					width={240}
					ellipsis
				/>

				<Table.Column title={txt('oplevalt.url')} render={renderLink} width={240} />

				<Table.Column
					dataIndex="created"
					title={txt('oplevalt.created')}
					render={renderDate}
					width={180}
				/>

				<Table.Column
					dataIndex="updated"
					title={txt('oplevalt.updated')}
					render={renderDate}
					width={180}
				/>
			</SharedTable>

			<ImageCatalogue
				useVisible={[openImageCatalogue, setOpenImageCatalogue]}
				sourcePrefix={OplevaltContext.imageSourcePrefix}
				onQuery={ImageService.query}
				onCreate={ImageService.create}
				onRemove={ImageService.remove}
			/>
		</MainStyled>
	);
};

const MainStyled = styled.main`
	display: flex;
	flex-direction: column;
	align-items: center;
	overflow: auto;

	.ant-switch-checked {
		background-color: ${OplevaltContext.theme.orange.vibrant};
	}

	.header-container {
		background-color: white;
		position: sticky;
		top: 0;
		width: 100%;
		padding: 0.4rem 1rem;
		z-index: 1;
		box-shadow: 0 0 1rem #ddd9;

		display: flex;
		align-items: center;
		justify-content: space-between;

		.ant-input-affix-wrapper {
			width: auto;
		}
	}

	a.ant-typography {
		color: ${OplevaltContext.theme.orange.vibrant};
	}

	.ant-btn-primary {
		background-color: ${OplevaltContext.theme.orange.vibrant};
		border-color: ${OplevaltContext.theme.orange.vibrant};
	}

	.action-buttons-container {
		display: flex;
		justify-content: center;
		gap: 1rem;
	}

	td.ant-table-cell.drag-visible {
		color: ${OplevaltContext.theme.orange.vibrant};
		cursor: grab;
	}

	tbody tr:nth-child(odd) {
		td {
			background: ${OplevaltContext.theme.orange.opaque};
		}
	}

	tbody tr:hover {
		td {
			background: ${OplevaltContext.theme.orange.neutral};
		}

		td:first-child {
			border-left: 3px solid ${OplevaltContext.theme.orange.vibrant};
		}
	}

	.ant-spin-dot-item {
		background-color: ${OplevaltContext.theme.orange.vibrant};
	}
`;
